import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Paper,
  Chip,
} from "@mui/material";
import { Print as PrintIcon, Receipt as ReceiptIcon, ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { Cookies } from "react-cookie";
import { useNavigate } from "react-router-dom";
import { config } from "../../../config/config";
import { apiList, invokeApi } from "../../../services/apiServices";
import { InvoiceDocument, printInvoiceElement, PURPLE } from "../../../componants/common/InvoiceDocument";

const GetInvoice = () => {
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [invoicesList, setInvoicesList] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [openDialog, setOpenDialog] = useState(true);
  const invoiceRef = useRef();

  useEffect(() => {
    fetchInvoicesList();
  }, []);

  const fetchInvoicesList = async () => {
    setLoadingList(true);
    try {
      const r = await invokeApi(config.getMyCollege + apiList.getInvoices, {}, Cookies);
      if (r?.status === 200) {
        setInvoicesList(r.data?.invoices || []);
      } else {
        setError(r?.responseMessage || "Failed to load invoices list");
      }
    } catch {
      setError("Failed to load invoices list");
    } finally {
      setLoadingList(false);
    }
  };

  const fetchInvoice = async (invoiceId) => {
    setLoading(true);
    setInvoice(null);
    try {
      const r = await invokeApi(config.getMyCollege + apiList.getInvoice, { invoiceId }, Cookies);
      if (r?.status === 200) {
        setInvoice(r.data.invoice);
        setOpenDialog(false);
      } else {
        setError(r?.responseMessage || "Failed to load invoice");
      }
    } catch {
      setError("Failed to load invoice");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    if (invoiceRef.current) {
      printInvoiceElement(invoiceRef.current, `Invoice-${invoice?.invoice_number || "AR-IN"}`);
    }
  };

  return (
    <Box sx={{ backgroundColor: "#F4F5F9", minHeight: "100vh", p: { xs: 1.5, md: 3 } }}>
      {/* Top Bar Actions */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2.5,
          flexWrap: "wrap",
          gap: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/dashboard/invoices")}
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: 1.5 }}
          >
            Invoice Dashboard
          </Button>
          <Typography variant="h6" sx={{ fontWeight: 800, color: "#1A237E" }}>
            Invoice Viewer
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ReceiptIcon />}
            onClick={() => setOpenDialog(true)}
            sx={{
              borderColor: PURPLE,
              color: PURPLE,
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 1.5,
            }}
          >
            Select Another Invoice
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            disabled={!invoice}
            sx={{
              backgroundColor: PURPLE,
              "&:hover": { backgroundColor: "#6454DB" },
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 1.5,
            }}
          >
            Download / Print PDF
          </Button>
        </Box>
      </Box>

      {/* Invoice Selector Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => invoice && setOpenDialog(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: "#1A237E", pb: 1 }}>
          Select an Invoice to View & Print
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {loadingList ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="160px">
              <CircularProgress size={32} sx={{ color: PURPLE }} />
            </Box>
          ) : invoicesList.length > 0 ? (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: PURPLE }}>
                  <TableCell sx={{ color: "#fff", fontWeight: 700 }}>#</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: 700 }}>Invoice No.</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: 700 }}>Customer Name</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: 700 }}>Phone</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: 700 }}>Total Amount</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: 700 }} align="center">
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoicesList.map((inv, idx) => (
                  <TableRow
                    key={inv.id}
                    hover
                    onClick={() => fetchInvoice(inv.id)}
                    sx={{ cursor: "pointer", "&:hover": { backgroundColor: "#F4F3FB" } }}
                  >
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: "#1A237E" }}>
                      {inv.invoice_number || inv.invoiceNumber}
                    </TableCell>
                    <TableCell>{inv.customer_name || inv.customerName}</TableCell>
                    <TableCell>{inv.customer_number || inv.customerNumber}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>₹ {inv.total}</TableCell>
                    <TableCell>
                      <Chip
                        label={inv.payment_status || "Pending"}
                        size="small"
                        color={
                          inv.payment_status === "Paid"
                            ? "success"
                            : inv.payment_status === "Partial"
                            ? "info"
                            : "warning"
                        }
                        sx={{ fontWeight: 700, fontSize: "11px" }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Button size="small" sx={{ color: PURPLE, fontWeight: 700, textTransform: "none" }}>
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Typography align="center" sx={{ p: 4, color: "#777" }}>
              No invoices generated yet.
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      {/* Loading state */}
      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="280px">
          <CircularProgress sx={{ color: PURPLE }} />
        </Box>
      )}

      {/* Error state */}
      {error && !loading && (
        <Paper sx={{ p: 3, textAlign: "center", bgcolor: "#FFF4F2", border: "1px solid #FFCDD2" }}>
          <Typography color="error" sx={{ fontWeight: 600 }}>
            {error}
          </Typography>
        </Paper>
      )}

      {/* Render Invoice Document */}
      {invoice && !loading && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
          <InvoiceDocument data={invoice} invoiceRef={invoiceRef} />
        </Box>
      )}
    </Box>
  );
};

export default GetInvoice;
